import React, { useEffect, useState } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, signOut } from '../AuthContext';
import { useJobs, jobIsActive, useLiveConfig } from '../data/firestore';
import { useMyOrgs } from '../data/entities';
import { SECTIONS, MODULES } from '../data/consoleModules';
import { ThemeToggle } from '../theme';
import { NavGroup, NavItem, NavKicker } from './Navbar';
import ConsoleTools from './ConsoleHeader';
import { openBugReport, openFeatureRequest } from './BugReportDialog';

// The signed-in bar outside an organization, on both hosts (decision
// signed-in-work-bar). Product first: the org you last worked in is the
// target, and the four spine sections (Build · Ship · Live · Organization,
// from the console catalog — the same table as the sidebar and ⌘K) open to
// every module in it. Console search (⌘K) and the notifications bell come
// along for that org. The overhead — Jobs, Help, Admin (founders), theme,
// account (with the public site folded in) — sits at the right edge.
// Inside /orgs/:id the OrgStrip (PlatformNav.js) takes over; visitors keep
// the marketing Navbar.
//
// `external`: on app.nventr.io the marketing and member-only public routes
// (/docs, /plan, /items …) do not exist, so those links point at nventr.io.
export const FOUNDERS = ['jitochadha@gmail.com', 'jito@nventr.ai'];

const SITE = 'https://nventr.io';
const LAST_ORG = 'nv.lastOrgId';

export default function WorkNav({ external = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const jobs = useJobs(100, true);
  const activeJobs = (jobs || []).filter(jobIsActive).length;
  const { doc: liveCfg } = useLiveConfig();
  const founder = FOUNDERS.includes(user.email);
  const handbookReader = founder || (liveCfg?.docs?.engineeringReaders || []).includes(user.email);

  // Target org: last used if still a member, else the first one. Picking
  // another re-targets the menus and the tools; it does not navigate.
  const orgs = useMyOrgs();
  const [picked, setPicked] = useState(() => { try { return localStorage.getItem(LAST_ORG); } catch { return null; } });
  const org = (orgs || []).find((o) => o.id === picked) || (orgs || [])[0] || null;
  const pickOrg = (id) => {
    if (id === '__all') { navigate('/orgs'); return; }
    setPicked(id);
    try { localStorage.setItem(LAST_ORG, id); } catch { /* ignore */ }
  };

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const close = () => setOpen(false);
  const under = (...prefixes) => prefixes.some((p) => location.pathname.startsWith(p));
  const initial = (user.displayName || user.email || '?').trim().charAt(0).toUpperCase();

  // Public-host-only routes: same-app links there, nventr.io links on the app host.
  const site = (to, label, end) => (external
    ? <a href={`${SITE}${to}`} onClick={close}>{label}</a>
    : <NavLink to={to} end={end} onClick={close}>{label}</NavLink>);

  const base = org ? `/orgs/${org.id}` : null;

  return (
    <nav className={`cs-nav scrolled cs-nav-work ${open ? 'open' : ''}`} aria-label="Platform">
      <Link to="/console" className="cs-brand" onClick={close} title="Your console">
        <img src="/logo.png" alt="nventr" className="cs-brand-logo" />
      </Link>
      {org && (
        <select
          className="cs-input pf-org-switcher"
          value={org.id}
          aria-label="Organization"
          title="The organization the menus and search below work in"
          onChange={(e) => pickOrg(e.target.value)}
        >
          {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          <option value="__all">All organizations…</option>
        </select>
      )}
      <div className="cs-nav-links cs-nav-links-modules">
        {orgs && !orgs.length && (
          <NavLink to="/orgs" onClick={close}>Create an organization →</NavLink>
        )}
        {org && SECTIONS.map((section) => (
          <NavGroup key={section.id} label={section.label} menu>
            {section.id === 'build' && (
              <>
                <NavKicker>Start</NavKicker>
                <NavItem to={base} end hint="Usage, cost, health and activity" onClick={close}>Home</NavItem>
                <NavItem to={`${base}/solutions`} hint="App kits on this foundation and apps on their own host" onClick={close}>Solutions</NavItem>
              </>
            )}
            <NavKicker>{section.tag}</NavKicker>
            {MODULES.filter((m) => m.section === section.id).map((mod) => (
              <NavItem key={mod.to} to={`${base}/${mod.to}`} hint={mod.tag} onClick={close}>{mod.label}</NavItem>
            ))}
          </NavGroup>
        ))}
      </div>
      {org && <ConsoleTools orgId={org.id} />}
      <button
        type="button"
        className="cs-nav-burger"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
      >
        {open ? '✕' : '☰'}
      </button>
      <div className="cs-nav-spacer" />
      <div className="cs-nav-links cs-nav-links-overhead">
        <NavLink to="/streams" onClick={close} title="Parallel Dev Consoles across your organizations">Streams</NavLink>
        <NavLink to="/jobs" className="cs-nav-jobs" onClick={close} title="Background jobs across your organizations">
          Jobs
          {activeJobs > 0 && <span className="cs-nav-jobs-badge">{activeJobs}</span>}
        </NavLink>
        <NavGroup label="Help" active={under('/support', '/bugs', '/features', '/docs')}>
          <NavLink to="/support" onClick={close}>Support</NavLink>
          <button type="button" className="cs-nav-linkbtn" onClick={() => { close(); openBugReport(); }}>Report a bug</button>
          <NavLink to="/bugs" onClick={close}>Your bug reports</NavLink>
          <button type="button" className="cs-nav-linkbtn" onClick={() => { close(); openFeatureRequest(); }}>Request a feature</button>
          <NavLink to="/features" onClick={close}>Feature board</NavLink>
          {site('/docs', 'Docs')}
        </NavGroup>
        {(founder || handbookReader) && (
          <NavGroup label="Admin" active={under('/admin', '/invites')}>
            {founder && (
              <>
                <NavLink to="/admin/apps" onClick={close}>Fleet</NavLink>
                <NavLink to="/admin/support" onClick={close}>Support desk</NavLink>
                <NavLink to="/admin/bugs" onClick={close}>Bug reports</NavLink>
                <NavLink to="/admin/helpdesk" onClick={close}>Help desk</NavLink>
                <NavLink to="/admin/features" onClick={close}>Feature requests</NavLink>
                <NavLink to="/admin/brain" onClick={close}>Brain</NavLink>
                <NavLink to="/admin/films" onClick={close}>Films</NavLink>
                <NavLink to="/invites" onClick={close}>Invites</NavLink>
              </>
            )}
            {handbookReader && <NavLink to="/admin/engineering" onClick={close}>Engineering handbook</NavLink>}
          </NavGroup>
        )}
      </div>
      <div className="cs-nav-user cs-nav-user-work">
        <ThemeToggle />
        <NavGroup
          label={(
            <>
              <span className="cs-nav-avatar" aria-hidden="true">{initial}</span>
              <span className="cs-nav-avatar-text">Account</span>
            </>
          )}
        >
          <div className="cs-nav-menu-who">{user.email}</div>
          <NavKicker>nventr.io</NavKicker>
          {site('/', 'Home', true)}
          {site('/pricing', 'Pricing')}
          {site('/contact?subject=Sales', 'Contact sales')}
          <NavKicker>Account</NavKicker>
          {site('/plan', 'Partner sign-in')}
          {site('/idp-pricing', 'IDP rate card')}
          {site('/items', 'Items')}
          <button type="button" className="cs-nav-linkbtn" onClick={() => signOut()} data-agent-noclick>Sign out</button>
        </NavGroup>
      </div>
    </nav>
  );
}
